import { useCallback, useEffect, useId, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from '@mypoz/i18n';
import type { FiuuTopUpErrorKind } from '@mypoz/shared';
import {
  FiuuTopUpError,
  RmTopUpError,
  TOP_UP_MAX_CENTS,
  TOP_UP_MIN_CENTS,
  isSafeRmPayUrl,
  isSafeTopUpPayUrl,
  toFiuuAmountParam,
  isValidTopUpAmount,
  parseTopUpAmountCents,
} from '@mypoz/shared';
import { Card, Icon, cn } from '@mypoz/ui';

import { createFiuuTopUp, createRmTopUp, fetchFiuuTopUpState } from '@/features/wallet/api.js';
import { TopUpAbandonedDialog } from '@/features/wallet/components/TopUpAbandonedDialog.js';
import { TopUpMethodPicker } from '@/features/wallet/components/TopUpMethodPicker.js';
import {
  FIUU_FPX_FEE_CENTS,
  FIUU_TOPUP_ENABLED,
  RM_EWALLET_FEE_CENTS,
  RM_TOPUP_ENABLED,
} from '@/features/wallet/config.js';
import { clearLastTopUp, readLastTopUp, saveLastTopUp } from '@/features/wallet/lastTopUpOrder.js';
import {
  isTopUpMethod,
  readLastTopUpMethod,
  saveLastTopUpMethod,
  type TopUpMethod,
} from '@/features/wallet/topUpMethod.js';
import { MYPOZ_SITE_URL } from '@/lib/legacy-api.js';
import { useFormat } from '@/lib/format.js';
import { actionLinkClass, buttonClass } from '@/lib/styles.js';
import { WALLET_TOP_UP_RESULT_ROUTE } from '@/shell/navigation.js';

/** RM 50 / 100 / 200 / 500 — the old site's 50/100/200 plus one larger step. In cents. */
const QUICK_AMOUNTS_CENTS = [5_000, 10_000, 20_000, 50_000];

/**
 * Router state the result page passes back with "Try again", so the same amount is filled in. Read
 * from router state, not the address, and only used if it is still a valid amount. `topUpMethod`
 * is optional: without it the card comes back to the method last picked in this tab.
 */
export interface TopUpFormLocationState {
  readonly topUpAmountCents?: number;
  readonly topUpMethod?: TopUpMethod;
}

/** Each method's own switch: Pay only calls the vendor for a method that is switched on. */
const METHOD_ENABLED: Record<TopUpMethod, boolean> = {
  fpx: FIUU_TOPUP_ENABLED,
  ewallet: RM_TOPUP_ENABLED,
};

/**
 * Each method's fee, in cents, as shown — never worked out. `undefined` while it is unknown (the
 * e-wallet fee today): the card then shows a dash for the fee and for what reaches the wallet.
 */
const METHOD_FEE_CENTS: Record<TopUpMethod, number | undefined> = {
  fpx: FIUU_FPX_FEE_CENTS,
  ewallet: RM_EWALLET_FEE_CENTS,
};

/**
 * How each method starts a top-up, and the one place its payment page may be: online banking asks
 * `molpay_pay` and goes to the vendor's gateway page; e-wallet asks `rm_pay` and goes straight to
 * RM's checkout page. Both answer with the top-up's record id, so the rest is the same.
 */
const METHOD_START: Record<
  TopUpMethod,
  {
    readonly start: (amountCents: number) => Promise<{ accdetailid: string; payUrl: string }>;
    readonly isSafePayUrl: (url: unknown) => boolean;
  }
> = {
  fpx: { start: createFiuuTopUp, isSafePayUrl: isSafeTopUpPayUrl },
  ewallet: { start: createRmTopUp, isSafePayUrl: isSafeRmPayUrl },
};

/** Whole cents → what the amount box shows, e.g. `10000` → `"100.00"` — from the digits, no float. */
const toAmountText = (cents: number) => toFiuuAmountParam(cents);

type Feedback =
  | { readonly kind: 'none' }
  | { readonly kind: 'starting' }
  | { readonly kind: 'notConnected' }
  | { readonly kind: 'error'; readonly reason: FiuuTopUpErrorKind };

/**
 * The "Top up" card on the Wallet page (MYP-134, MYP-135).
 *
 * The customer picks how to pay — online banking (Fiuu FPX) or e-wallet / DuitNow QR (Revenue
 * Monster), the old site's 付款方式 — then picks or types an amount, sees what reaches the wallet,
 * and presses Pay. The bank or the wallet is chosen on the gateway's own page, so there is no list
 * of them here.
 *
 * Online banking: Pay asks the vendor to start the top-up (`createFiuuTopUp`, `molpay_pay`): it
 * answers with the link to its gateway page, already checked to be that page and for this amount,
 * and the browser goes there; the gateway forwards the customer to Fiuu.
 *
 * E-wallet: Pay asks the vendor the same way (`createRmTopUp`, `rm_pay`), and the browser goes
 * straight to RM's checkout page. Either way the vendor sends the customer back to the same result
 * page, and Back from the payment page is handled the same.
 *
 * A method whose switch is off never calls anything: Pay says top-up that way is not switched on
 * yet, with a link to mypoz.com where it works today.
 */
export function TopUpForm() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const amountId = useId();
  const hintId = useId();

  const retryCents = (useLocation().state as TopUpFormLocationState | null)?.topUpAmountCents;
  const [amountText, setAmountText] = useState(() =>
    typeof retryCents === 'number' && isValidTopUpAmount(retryCents)
      ? toAmountText(retryCents)
      : '',
  );
  const retryMethod = (useLocation().state as TopUpFormLocationState | null)?.topUpMethod;
  const [method, setMethod] = useState<TopUpMethod>(() =>
    isTopUpMethod(retryMethod) ? retryMethod : (readLastTopUpMethod() ?? 'fpx'),
  );
  const [touched, setTouched] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>({ kind: 'none' });
  /** The "Payment not completed" dialog: unpaid when the check said so, unchecked when it could not tell. */
  const [abandoned, setAbandoned] = useState<'unpaid' | 'unchecked' | undefined>(undefined);
  const navigate = useNavigate();

  /*
    Back from the payment page. Pay saves the top-up's id just before the browser leaves for the
    gateway, and the result page clears it as soon as it takes over — so an id still saved here
    means the customer came back to this card without finishing. Two ways back:

    - the browser shows its in-memory copy of this page (the back/forward cache): no React code
      runs, the card is frozen on "Starting…" — `pageshow` with `persisted` is the only signal;
    - the browser loads the page afresh: the card mounts, and finds the id on its way in.

    Either way: unfreeze the card, forget the id (so this happens once), and look the top-up up
    once. Paid after all (paid at the bank, then pressed Back) → the result page says so. Unpaid,
    or not found → "Payment not completed": the vendor has recorded it as unpaid and its link must
    not be reused, so the customer starts a new one. No answer → the same dialog, but saying the
    app could not check, and pointing at the top-up history before paying again — a payment still
    being credited must not be paid twice. The amount stays filled in.
  */
  const checkAbandoned = useCallback(() => {
    const accdetailid = readLastTopUp();
    if (accdetailid === undefined) return;
    clearLastTopUp();
    setFeedback({ kind: 'none' });
    fetchFiuuTopUpState({ accdetailid }).then(
      (state) => {
        if (state?.status === 'paid') {
          void navigate(
            `${WALLET_TOP_UP_RESULT_ROUTE}?accdetailid=${encodeURIComponent(accdetailid)}`,
          );
        } else {
          setAbandoned('unpaid');
        }
      },
      () => setAbandoned('unchecked'),
    );
  }, [navigate]);

  useEffect(() => {
    checkAbandoned();
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) checkAbandoned();
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [checkAbandoned]);

  const closeAbandoned = () => {
    setAbandoned(undefined);
    document.getElementById(amountId)?.focus();
  };

  const parsed = parseTopUpAmountCents(amountText);
  /** The amount to pay, once it is a real amount within the limits; undefined until then. */
  const amountCents = parsed !== undefined && isValidTopUpAmount(parsed) ? parsed : undefined;
  const valid = amountCents !== undefined;
  const showInvalid = touched && amountText.trim() !== '' && !valid;
  const starting = feedback.kind === 'starting';
  const limits = {
    min: fmt.money(TOP_UP_MIN_CENTS),
    max: fmt.money(TOP_UP_MAX_CENTS),
  };

  const feeCents = METHOD_FEE_CENTS[method];
  const addedCents =
    amountCents !== undefined && feeCents !== undefined ? amountCents - feeCents : undefined;

  const changeMethod = (next: TopUpMethod) => {
    setMethod(next);
    saveLastTopUpMethod(next);
    if (feedback.kind !== 'starting') setFeedback({ kind: 'none' });
  };

  const changeAmount = (text: string) => {
    setAmountText(text);
    if (feedback.kind !== 'starting') setFeedback({ kind: 'none' });
  };

  const pickQuickAmount = (cents: number) => {
    changeAmount(toAmountText(cents));
    setTouched(true);
  };

  const pay = async (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (amountCents === undefined || starting) return;

    if (!METHOD_ENABLED[method]) {
      setFeedback({ kind: 'notConnected' });
      return;
    }

    setFeedback({ kind: 'starting' });
    try {
      const start = await METHOD_START[method].start(amountCents);
      // Checked already by the normaliser; checked again at the one place the browser leaves.
      if (!METHOD_START[method].isSafePayUrl(start.payUrl)) {
        setFeedback({ kind: 'error', reason: 'unusable' });
        return;
      }
      saveLastTopUp(start.accdetailid);
      window.location.assign(start.payUrl);
    } catch (error) {
      console.error(`[wallet] ${method} top-up could not start`, error);
      setFeedback({
        kind: 'error',
        reason:
          error instanceof FiuuTopUpError || error instanceof RmTopUpError
            ? error.kind
            : 'unusable',
      });
    }
  };

  return (
    <Card>
      <form
        onSubmit={(event) => void pay(event)}
        noValidate
        className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] lg:gap-8"
      >
        <div className="flex flex-col gap-4">
          <div>
            <h2 className="text-base font-semibold text-ink">{t('wallet.topUp.title')}</h2>
            <p className="mt-1 text-sm text-muted">{t('wallet.topUp.intro')}</p>
          </div>

          <TopUpMethodPicker value={method} onChange={changeMethod} disabled={starting} />

          <div>
            <label htmlFor={amountId} className="mb-2 block text-sm font-semibold text-ink">
              {t('wallet.topUp.amountStep')}
            </label>

            <div
              role="group"
              aria-label={t('wallet.topUp.quickAmounts')}
              className="mb-2.5 grid grid-cols-4 gap-2"
            >
              {QUICK_AMOUNTS_CENTS.map((cents) => {
                const active = amountCents === cents;
                return (
                  <button
                    key={cents}
                    type="button"
                    aria-pressed={active}
                    onClick={() => pickQuickAmount(cents)}
                    className={cn(
                      'min-h-11 rounded-md border px-2 text-sm transition-colors lg:min-h-10',
                      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
                      active
                        ? 'border-brand bg-brand-soft font-semibold text-brand'
                        : 'border-line bg-surface text-ink hover:bg-surface-alt',
                    )}
                  >
                    {t('wallet.topUp.quickAmount', { value: fmt.number(cents / 100, 0) })}
                  </button>
                );
              })}
            </div>

            <div
              className={cn(
                'flex min-h-12 items-center gap-2 rounded-md border bg-surface px-3',
                'focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-brand',
                showInvalid ? 'border-danger' : 'border-line-strong',
              )}
            >
              <span aria-hidden="true" className="text-sm font-semibold text-muted">
                {t('wallet.topUp.currency')}
              </span>
              <input
                id={amountId}
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={amountText}
                placeholder={fmt.number(0, 2)}
                onChange={(event) => changeAmount(event.target.value)}
                onBlur={() => setTouched(true)}
                aria-invalid={showInvalid}
                aria-describedby={hintId}
                className="min-w-0 flex-1 bg-transparent text-base font-semibold text-ink outline-none placeholder:font-normal placeholder:text-muted-soft"
              />
            </div>
            <p
              id={hintId}
              role={showInvalid ? 'alert' : undefined}
              className={cn('mt-1.5 text-sm', showInvalid ? 'text-danger' : 'text-muted')}
            >
              {showInvalid ? t('wallet.topUp.invalid', limits) : t('wallet.topUp.limits', limits)}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3 rounded-lg bg-surface-alt p-4 sm:p-5">
          <div className="flex justify-between gap-3 text-sm">
            <span className="text-muted">{t('wallet.topUp.payWith')}</span>
            <span className="text-right font-semibold text-ink">
              {t(`wallet.topUp.methods.${method}.summary`)}
            </span>
          </div>
          <div className="flex justify-between gap-3 text-sm">
            <span className="text-muted">{t('wallet.topUp.amountLabel')}</span>
            <span className="text-ink tabular-nums">
              {amountCents !== undefined ? fmt.money(amountCents) : '—'}
            </span>
          </div>
          <div className="flex justify-between gap-3 text-sm">
            <span className="text-muted">{t(`wallet.topUp.methods.${method}.fee`)}</span>
            <span className="text-ink tabular-nums">
              {feeCents !== undefined ? fmt.money(feeCents) : '—'}
            </span>
          </div>
          <div className="h-px bg-line" />
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm font-semibold text-ink">{t('wallet.topUp.added')}</span>
            <span className="text-xl font-semibold text-ink tabular-nums">
              {addedCents !== undefined ? fmt.money(addedCents) : '—'}
            </span>
          </div>

          <button
            type="submit"
            disabled={starting}
            className={buttonClass(
              'primary',
              'min-h-12 w-full text-base font-semibold disabled:cursor-wait disabled:opacity-70 lg:min-h-11',
            )}
          >
            <Icon name="lock" size={18} />
            {starting
              ? t('wallet.topUp.starting')
              : amountCents !== undefined
                ? t(`wallet.topUp.methods.${method}.pay`, { amount: fmt.money(amountCents) })
                : t(`wallet.topUp.methods.${method}.payNoAmount`)}
          </button>

          {feedback.kind === 'notConnected' ? (
            <div role="status" className="rounded-md border border-line bg-surface p-3 text-sm">
              <p className="text-ink">{t(`wallet.topUp.methods.${method}.notConnected`)}</p>
              <a
                href={MYPOZ_SITE_URL}
                target="_blank"
                rel="noreferrer"
                className={actionLinkClass('mt-1 inline-flex text-sm')}
              >
                {t('wallet.topUp.goToMypoz')}
              </a>
            </div>
          ) : null}
          {feedback.kind === 'error' ? (
            <p role="alert" className="text-sm font-medium text-danger">
              {feedback.reason === 'unavailable'
                ? t(`wallet.topUp.methods.${method}.unavailable`)
                : t(`wallet.topUp.errors.${feedback.reason}`)}
            </p>
          ) : null}

          <ul className="flex list-disc flex-col gap-1 pl-5 text-xs text-muted">
            <li>{t(`wallet.topUp.methods.${method}.secure`)}</li>
            <li>{t(`wallet.topUp.methods.${method}.note`)}</li>
            <li className="font-medium text-danger">{t('wallet.topUp.noRefund')}</li>
          </ul>
        </div>
      </form>
      {abandoned !== undefined ? (
        <TopUpAbandonedDialog checked={abandoned === 'unpaid'} onClose={closeAbandoned} />
      ) : null}
    </Card>
  );
}
